import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { CopyButton } from '../components/CopyButton';
import { ClassificationBadge } from '../components/Classification';
import { Ownership, type Owner } from '../components/Ownership';
// Shared compact relative-time formatter ("20h ago"). Declared in the
// agent-commands lib but explicitly a shared formatter there; importing it beats
// a second copy that can drift on the unit ladder or the clock-skew clamp.
import { timeAgo } from '../lib/agentCommands';
import { useCrumb } from '../lib/crumb';
import { useMe } from '../lib/useMe';
import { formatDateTime, formatDuration } from '../lib/format';
import { DetailHead } from '../components/DetailHead';
import { DetailBody, RailCard } from '../components/DetailRail';
import { compareBy, useUrlNumber, useUrlSort } from '../lib/useUrlState';
import { Icon } from '../components/Icon';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { RefreshButton } from '../components/RefreshButton';

const REPO = gql`
  query Repo($id: Float!) {
    repo(gitlabProjectId: $id) {
      id
      gitlabProjectId
      pathWithNamespace
      groupPath
      visibility
      defaultBranch
      archived
      lastActivityAt
      deploymentCount
      classification
      productLine
      productLineConfidence
      service {
        id
        name
        classification
        catalogName
        description
        repoCount
      }
      owners {
        ownerId
        role
        gitlabUsername
        displayName
        email
      }
      sourceScanner
      firstSeenAt
      lastSeenAt
      confidence
      inheritedEnv {
        packageName
        pinnedVersion
        contractVersion
        layers {
          layer
          vars
        }
      }
      deployments {
        id
        cluster
        namespace
        name
        kind
        replicas
        imageTag
        lastSeenAt
        env {
          name
          source
        }
        envFrom {
          kind
          name
        }
      }
    }
  }
`;

// Alerts card — only fetched once we know the repo's service. `serviceAlertSelector`
// comes back as an opaque JSON string ({matchers, source}) rather than a typed object:
// it mirrors labelsJson elsewhere on this page, and keeps the wire shape identical to
// whatever setServiceAlertSelector's matchersJson argument expects.
const REPO_ALERTS = gql`
  query RepoAlerts($serviceId: String!) {
    alertsMonitored
    serviceAlerts(serviceId: $serviceId) {
      alertname
      sev
      activeAt
      summary
      runbookUrl
      grafanaExploreUrl
      grafanaAlertUrl
    }
    serviceAlertSelector(serviceId: $serviceId)
  }
`;

const SET_SELECTOR = gql`
  mutation SetServiceAlertSelector($serviceId: String!, $matchersJson: String!) {
    setServiceAlertSelector(serviceId: $serviceId, matchersJson: $matchersJson)
  }
`;

interface RepoAlertRow {
  alertname: string;
  sev?: string;
  activeAt: string;
  summary?: string;
  runbookUrl?: string;
  grafanaExploreUrl: string;
  grafanaAlertUrl: string;
}
interface RepoAlertsData {
  alertsMonitored: boolean;
  serviceAlerts: RepoAlertRow[];
  serviceAlertSelector: string | null;
}

interface EnvVar {
  name: string;
  source: string;
}
interface EnvFrom {
  kind: string;
  name: string;
}
interface Deployment {
  id: string;
  cluster: string;
  namespace: string;
  name: string;
  kind: string;
  replicas?: number;
  imageTag?: string;
  lastSeenAt: string;
  env: EnvVar[];
  envFrom: EnvFrom[];
}
interface InheritedLayer {
  layer: string;
  vars: string[];
}
interface InheritedCommon {
  packageName: string;
  pinnedVersion?: string;
  contractVersion?: string;
  layers: InheritedLayer[];
}
interface Service {
  id: string;
  name: string;
  classification?: string;
  catalogName?: string;
  description?: string;
  repoCount: number;
}
interface Repo {
  id: string;
  gitlabProjectId: number;
  pathWithNamespace: string;
  groupPath?: string;
  visibility?: string;
  defaultBranch?: string;
  archived?: boolean;
  lastActivityAt?: string;
  deploymentCount: number;
  classification: string;
  productLine?: string;
  productLineConfidence?: number;
  service?: Service | null;
  sourceScanner: string;
  firstSeenAt: string;
  lastSeenAt: string;
  confidence: string;
  inheritedEnv: InheritedCommon[];
  deployments: Deployment[];
  owners: Owner[];
}
interface Data {
  repo: Repo | null;
}

type Tab = 'overview' | 'deployments';
const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'deployments', label: 'Deployments' },
];

export function RepoDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const { data, loading, error, refetch } = useQuery<Data>(REPO, {
    variables: { id: Number(id) },
    fetchPolicy: 'cache-and-network',
  });

  const r = data?.repo;

  // Breadcrumb tail — the topbar knows the route, not the entity.
  // MUST sit above the early returns: called after them, it would be a
  // conditional hook and React would throw once the data arrived.
  useCrumb(data?.repo?.pathWithNamespace?.split('/').pop());

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error) return <div className="page muted">Failed to load: {error.message}</div>;
  if (!r)
    return (
      <div className="page">
        <p className="muted">Repo not found.</p>
        <Link to="/repos">← Back to repos</Link>
      </div>
    );

  const shortName = r.pathWithNamespace.split('/').pop();

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/repos" className="muted">
          ← Repos
        </Link>
      </div>
      <DetailHead
        name={r.pathWithNamespace}
        badge={<ClassificationBadge value={r.classification} />}
        facts={[
          r.productLine && (
            <span title={`product line (${r.productLineConfidence ?? '—'}% confidence)`}>
              {r.productLine}
            </span>
          ),
          r.service?.catalogName && (
            <Link to={`/catalog/${r.service.catalogName}`}>{r.service.catalogName}</Link>
          ),
          r.archived && <span className="badge inferred">archived</span>,
          r.deploymentCount > 0 &&
            `${r.deploymentCount} deployment${r.deploymentCount === 1 ? '' : 's'}`,
          r.visibility,
        ]}
        scannedAt={r.lastSeenAt}
        actions={<CopyButton value={r.pathWithNamespace} />}
      />

      <DetailBody
        rail={
          <>
            {/* No RailCard wrapper: Ownership renders its own "Ownership"
                heading, and wrapping it printed the word twice. */}
            <div className="rail-card">
              <Ownership
                subjectType="repo"
                subjectId={r.id}
                owners={r.owners}
                onChanged={() => void refetch()}
              />
            </div>
            <RailCard title="Elsewhere">
              <div className="stack-sm">
                <a href={`https://gitlab.com/${r.pathWithNamespace}`} target="_blank" rel="noreferrer">
                  Open in GitLab <Icon name="external" />
                </a>
                {r.service?.name && (
                  <Link to={`/repos?view=graph&service=${encodeURIComponent(r.service.name)}`}>
                    Where it runs (graph)
                  </Link>
                )}
                {/* Prefilled so the reviewer gets the exact repo, not a retyped
                    approximation of it. */}
                <Link
                  to={`/tasks/new?template=access&type=repo&entityId=${r.id}&name=${encodeURIComponent(r.pathWithNamespace)}`}
                >
                  Request access
                </Link>
              </div>
            </RailCard>
          </>
        }
      >
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
            {t.key === 'deployments' && r.deploymentCount > 0 ? ` (${r.deploymentCount})` : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview r={r} />}
      {tab === 'deployments' && <Deployments deps={r.deployments} />}
      </DetailBody>
    </div>
  );
}

function Overview({ r }: { r: Repo }) {
  // Deliberately does NOT repeat the title row or the rail: classification is the
  // status badge, catalog/visibility/archived are the facts line, and the GitLab
  // link lives in the rail. Restating them here is how a detail page ends up
  // saying the same thing three times.
  const facts: [string, React.ReactNode][] = [
    [
      'Service',
      r.service
        ? `${r.service.name} (${r.service.repoCount} repo${r.service.repoCount === 1 ? '' : 's'})`
        : '—',
    ],
    ['Group', r.groupPath ?? '—'],
    ['Default branch', r.defaultBranch ?? '—'],
    [
      'GitLab project id',
      <span className="ip-cell">
        {r.gitlabProjectId}
        <CopyButton value={String(r.gitlabProjectId)} />
      </span>,
    ],
    ['Deployments (k8s)', r.deploymentCount],
    ['Last activity', r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleString() : '—'],
  ];  return (
    <>
      <div className="kv-grid">
        {facts.map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>

      {r.service && <AlertsCard serviceId={r.service.id} />}

      <InheritedEnv commons={r.inheritedEnv} />

      <h3 className="section">Provenance</h3>
      <div className="kv-grid">
        {(
          [
            ['Source scanner', r.sourceScanner],
            ['Confidence', r.confidence],
            ['First seen', new Date(r.firstSeenAt).toLocaleString()],
            ['Last seen', new Date(r.lastSeenAt).toLocaleString()],
          ] as [string, React.ReactNode][]
        ).map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>
    </>
  );
}


/**
 * Alerts card — firing alerts for this repo's service, its coverage grade, and
 * the declared selector. root|maintainer get an editable matchers textarea
 * ("Save selector" turns a suggested→human on the server, so the button reads
 * "Confirm suggestion" while nothing's been hand-edited yet); everyone else
 * reads the same matchers as chips. Only mounted when the repo has a service
 * (Overview's `r.service &&` guard) — "monitored" is a separate, env-wide
 * signal from "has a service", so its own not-monitored badge, not a hide.
 */
function AlertsCard({ serviceId }: { serviceId: string }) {
  const { role } = useMe();
  const canEdit = role === 'root' || role === 'maintainer';
  const { data, loading, error, refetch } = useQuery<RepoAlertsData>(REPO_ALERTS, {
    variables: { serviceId },
    fetchPolicy: 'cache-and-network',
  });
  const [setSelector, { loading: saving, error: saveError }] = useMutation(SET_SELECTOR, {
    onCompleted: () => void refetch(),
  });

  const selector = data?.serviceAlertSelector
    ? (JSON.parse(data.serviceAlertSelector) as { matchers: Record<string, string>[]; source: string })
    : null;
  const [draft, setDraft] = useState('');
  // Prefixed ('al') sort + page keys: keeps this embedded card's URL state clear
  // of anything else the repo page may ever put in the URL. 10/page — it is a
  // card, not the estate board.
  const sort = useUrlSort('sev', 'asc', 'al');
  const [page, setPage] = useUrlNumber('alpage', 1);
  const firingAll: RepoAlertRow[] = data?.serviceAlerts ?? [];
  const sortedFiring = useMemo<RepoAlertRow[]>(() => {
    const pick = (r: RepoAlertRow) =>
      sort.key === 'alertname' ? r.alertname : sort.key === 'since' ? r.activeAt : r.sev;
    return compareBy(firingAll, pick, sort.dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.serviceAlerts, sort.key, sort.dir]);
  const paged = usePaged(sortedFiring, 10, { page, onPage: setPage });
  useEffect(() => {
    // Re-derive from the server's copy only, not on every keystroke.
    setDraft(JSON.stringify(selector?.matchers ?? [], null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.serviceAlertSelector]);

  if (loading && !data)
    return (
      <>
        <h3 className="section">Alerts</h3>
        <p className="muted">Loading…</p>
      </>
    );
  if (error)
    return (
      <>
        <h3 className="section">Alerts</h3>
        <p className="error text-sm">Couldn&apos;t load alerts.</p>
      </>
    );
  if (!data) return null;

  const firing = paged.pageItems;
  const save = () => void setSelector({ variables: { serviceId, matchersJson: draft } });

  return (
    <>
      <div className="section-head">
        <h3>Alerts</h3>
        {!data.alertsMonitored && <span className="badge quiet">not monitored</span>}
        <span className="spacer" />
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>

      {!data.alertsMonitored ? (
        <p className="muted text-sm">
          This environment has no Alertmanager wired up yet, so there is nothing to show here.
        </p>
      ) : (
        <>
          {paged.total === 0 ? (
            <p className="muted text-sm">No alerts currently firing for this service.</p>
          ) : (
            <table className="data-list--primary">
              <thead>
                <tr>
                  <SortHeader label="Sev" sortKey="sev" sort={sort} />
                  <SortHeader label="Alert" sortKey="alertname" sort={sort} />
                  <SortHeader label="Since" sortKey="since" sort={sort} />
                  <th>Grafana</th>
                </tr>
              </thead>
              <tbody>
                {firing.map((a, i) => (
                  <tr key={`${a.alertname}-${i}`}>
                    <td>
                      <span
                        className={`badge ${a.sev === 'sev0' || a.sev === 'sev1' ? 'failed' : a.sev === 'sev2' ? 'inferred' : 'quiet'}`}
                      >
                        {a.sev ?? '—'}
                      </span>
                    </td>
                    <td className="table-title" title={a.summary}>
                      {a.alertname}
                    </td>
                    <td title={formatDateTime(a.activeAt)}>{formatDuration(a.activeAt, new Date().toISOString())} ago</td>
                    <td>
                      <a href={a.grafanaAlertUrl} target="_blank" rel="noreferrer" title="the alert rule in Grafana">
                        Alert
                      </a>
                      <span className="muted"> · </span>
                      <a href={a.grafanaExploreUrl} target="_blank" rel="noreferrer" title="logs for this cluster/namespace">
                        Logs
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pager
            page={paged.page}
            pageCount={paged.pageCount}
            total={paged.total}
            start={paged.start}
            pageSize={paged.pageSize}
            onPage={paged.setPage}
          />

        </>
      )}

      <div className="dep-card" style={{ marginTop: 10 }}>
        <div className="kv-k" style={{ marginBottom: 6 }}>
          Alert selector
          {selector && (
            <span
              className={`badge ${selector.source === 'human' ? 'confirmed' : 'inferred'}`}
              style={{ marginLeft: 8 }}
            >
              {selector.source}
            </span>
          )}
        </div>
        {canEdit ? (
          <>
            <textarea
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saving}
              style={{ width: '100%', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <button type="button" className="btn btn-sm" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : selector?.source === 'suggested' ? 'Confirm suggestion' : 'Save selector'}
              </button>
              {saveError && <span className="error text-xs">{saveError.message}</span>}
            </div>
          </>
        ) : selector ? (
          <div className="env-list">
            {selector.matchers.map((m, i) => (
              <span key={i} className="env-chip">
                {Object.entries(m)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(', ')}
              </span>
            ))}
          </div>
        ) : (
          <p className="muted text-xs" style={{ margin: 0 }}>
            No selector declared yet.
          </p>
        )}
      </div>
    </>
  );
}

function InheritedEnv({ commons }: { commons: InheritedCommon[] }) {
  if (commons.length === 0) return null;
  const totalVars = commons.reduce((n, c) => n + c.layers.reduce((m, l) => m + l.vars.length, 0), 0);
  return (
    <>
      <h3 className="section">Inherited env — shared common packages ({totalVars})</h3>
      <p className="muted text-xs" style={{ marginTop: -4 }}>
        Default env the app's <strong>backend + job</strong> layers read via shared common packages
        (from each package's source), on top of the per-deployment env below. Values not captured.
      </p>
      {commons.map((c) => (
        <div className="dep-card" key={c.packageName}>
          <div className="dep-head">
            <strong>{c.packageName}</strong>
            {c.pinnedVersion && <span className="tag-badge">pins: {c.pinnedVersion}</span>}
            {c.contractVersion && c.contractVersion !== c.pinnedVersion && (
              <span className="muted text-2xs">scanned {c.contractVersion}</span>
            )}
          </div>
          {c.layers.length === 0 ? (
            <p className="muted text-xs" style={{ margin: '6px 0 0' }}>
              No default env vars read by this package.
            </p>
          ) : (
            c.layers.map((l) => (
              <div key={l.layer} className="env-layer">
                <span className={`layer-badge layer-${l.layer}`}>{l.layer}</span>
                <div className="env-list">
                  {l.vars.map((v) => (
                    <span key={v} className="env-chip">
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ))}
    </>
  );
}

function Deployments({ deps }: { deps: Deployment[] }) {
  if (deps.length === 0)
    return (
      <p className="muted">
        No k8s deployments found for this repo's image. Either it isn't deployed, or its container
        image isn't a <code>registry.gitlab.com/optisigns/…</code> path the scanner matches.
      </p>
    );
  return (
    <>
      {/* One age for the whole section, not one per card — every row is written by the
          same scan run, so per-card it was the same string five times over.
          Uses the OLDEST row: a summary that showed the freshest would hide a stale
          one, and hiding staleness is the bug this whole line exists to prevent. */}
      <h3 className="section">
        Where it's deployed ({deps.length})
        <span
          className={`section-age${ageMs(oldestSeen(deps)) > STALE_AFTER_MS ? ' stale' : ''}`}
          title={
            `Last confirmed by a scan at ${new Date(oldestSeen(deps)).toLocaleString()}` +
            (deps.length > 1 ? ' (the oldest of these rows)' : '')
          }
        >
          synced {timeAgo(oldestSeen(deps))}
        </span>
      </h3>
      {deps.map((d) => (
        <DeploymentCard key={d.id} d={d} />
      ))}
      <p className="muted text-2xs" style={{ marginTop: 10 }}>
        Env var <strong>values are not captured</strong> — many are secrets (API keys, passwords).
        Only names + their source (literal / secretRef / configMapRef / …) are shown.
      </p>
    </>
  );
}

/** gitlab-scan runs every 30 min; past two missed runs the row is worth doubting. */
const STALE_AFTER_MS = 60 * 60 * 1000;

const ageMs = (iso: string) => Date.now() - new Date(iso).getTime();

/** The least recently confirmed row — the honest headline for a set of them. */
const oldestSeen = (deps: { lastSeenAt: string }[]) =>
  deps.reduce((oldest, d) => (d.lastSeenAt < oldest ? d.lastSeenAt : oldest), deps[0].lastSeenAt);

/**
 * Namespace → environment, by SEGMENT rather than exact name.
 *
 * An exact match on the four canonical names would miss `us-prod`,
 * `transforge-prod`, `us-beta`, `opticlaw-sandbox`, `qa3` — 40-odd live workloads,
 * including real production ones. An unbadged production deployment is precisely
 * the case the badge exists for, so the mapping has to cover the prefixed forms.
 *
 * Segment matching, not `includes()`: a namespace like `product-catalog` contains
 * "prod" but is not production. Splitting on `-` and comparing whole segments
 * avoids inventing an environment out of a substring.
 *
 * Unknown namespaces (`rest`, `cdp`, …) return null and render as plain text —
 * better than guessing an environment for something that has none.
 */
const ENV_BY_SEGMENT: Record<string, { label: string; cls: string }> = {
  production: { label: 'production', cls: 'prod' },
  prod: { label: 'production', cls: 'prod' },
  staging: { label: 'staging', cls: 'env-staging' },
  beta: { label: 'beta', cls: 'env-beta' },
  qa: { label: 'qa', cls: 'env-qa' },
  sandbox: { label: 'sandbox', cls: 'env-sandbox' },
  sbx: { label: 'sandbox', cls: 'env-sandbox' },
};

export function envOfNamespace(ns: string): { label: string; cls: string } | null {
  for (const seg of ns.toLowerCase().split('-')) {
    // qa3 / qa3a / beta3b / beta0 … the digits are the env INSTANCE, not a different
    // environment. `\d*` not `3`: `us-beta0` is live today and a hard-coded 3 missed it.
    const key = /^qa\d*[a-z]?$/.test(seg) ? 'qa' : /^beta\d*[a-z]?$/.test(seg) ? 'beta' : seg;
    if (ENV_BY_SEGMENT[key]) return ENV_BY_SEGMENT[key];
  }
  return null;
}

function DeploymentCard({ d }: { d: Deployment }) {
  const env = envOfNamespace(d.namespace);
  return (
    <div className="dep-card">
      <div className="dep-head">
        {/* The cluster is the other half of "where does this run" — every other
            page links its entities, this one dead-ended. */}
        <Link className="table-title" to={`/clusters/${encodeURIComponent(d.cluster)}`}>
          {d.cluster}
        </Link>
        {/* The namespace is ALWAYS shown, even when the badge repeats it. The badge
            is a derived classification (`us-prod` → production); the namespace is the
            literal fact you search the page for. Hiding it as "redundant" broke
            Cmd+F for a namespace name, which is how people actually find a
            deployment here. */}
        <span className="muted">/ {d.namespace}</span>
        {env && <span className={`badge ${env.cls}`}>{env.label}</span>}
        <span className="badge">{d.kind}</span>
        {d.imageTag && <span className="tag-badge">tag: {d.imageTag}</span>}
        <span className="muted" style={{ marginLeft: 'auto' }}>
          {d.replicas ?? '—'} replica{d.replicas === 1 ? '' : 's'}
        </span>
      </div>
      <div className="dep-workload muted">
        {d.name}
        <CopyButton value={d.name} />
      </div>

      {d.envFrom.length > 0 && (
        <div className="env-from">
          <span className="kv-k">Bulk sources (envFrom):</span>
          {d.envFrom.map((f) => (
            <span key={f.kind + f.name} className={`env-chip src-${f.kind === 'secret' ? 'secretRef' : 'configMapRef'}`}>
              {f.kind}:{f.name}
            </span>
          ))}
        </div>
      )}

      {d.env.length > 0 ? (
        <div className="env-list">
          {d.env.map((e) => (
            <span key={e.name} className={`env-chip src-${e.source}`} title={`source: ${e.source}`}>
              {e.name}
            </span>
          ))}
        </div>
      ) : d.envFrom.length === 0 ? (
        <p className="muted text-xs" style={{ margin: '6px 0 0' }}>
          No inline env vars declared on the Deployment spec.
        </p>
      ) : null}
    </div>
  );
}
